import axios from "axios";
import { filter, find, isNil, map, orderBy, take } from "lodash";
import route from "ziggy-js";
import { PromiseStatus } from "../Constants/api";
import { RouteName } from "../Constants/routes";
import { TYPE_APPLY, TYPE_SCOUT } from "../Constants/user-scout";
import { findConversationFromSid, getConversationFromSids, getValidConversationsFromSids } from "./ConversationService";

export const getTwilioToken = async ({ uid }) => {
    return await axios.post(route(RouteName.TwilioToken), { uid });
};


export const createConversation = async ({ client, friendlyName }) => {
    return await client.createConversation({ friendlyName });
};

export const getConversations = async ({ client }) => {
    let conversationList = [];

    try {
        let response = await client.getSubscribedConversations();
        let hasMore = response.hasNextPage;
        conversationList = [...conversationList, ...response.items];

        while (hasMore) {
            response = await response.nextPage();

            hasMore = response.hasNextPage;
            conversationList = [...conversationList, ...response.items];
        }
    } catch {
        return [];
    }

    const conversationSids = map(conversationList, (item) => item.sid);

    const validResponse = await getValidConversationsFromSids({ conversationSids });

    if (!validResponse.success) {
        return [];
    }

    return filter(conversationList, (conver) => find(validResponse.conversation_sids, (item) => item === conver.sid));
};

export const transformConversationList = async ({ client, dbConversationList, userUid }) => {
    let converListPromise = [];

    for (let dbItem of dbConversationList) {
        converListPromise.push(client.getConversationBySid(dbItem.conversation_id));
    }

    const promiseResult = await Promise.allSettled(converListPromise);

    let result = [];

    for (let promiseResultItem of promiseResult) {
        if (promiseResultItem.status === PromiseStatus.Rejected) {
            continue;
        }
        const promiseItem = promiseResultItem.value;
        const lastMessage = promiseItem.lastMessage;

        const participant = await promiseItem.getParticipantByIdentity(userUid);
        const lastReadMessageIndex = participant.lastReadMessageIndex;

        let unreadCount = 0;
        let messageItem = null;

        if (lastMessage) {
            const messages = await promiseItem.getMessages(1, lastMessage.index);
            messageItem = messages.items[0].body;

            unreadCount = isNil(lastReadMessageIndex) ? lastMessage.index + 1 : lastMessage.index - lastReadMessageIndex;
        }

        const dbItem = find(dbConversationList, (item) => item.conversation_id === promiseItem.sid);

        result.push({
            id: dbItem.id,
            sid: promiseItem.sid,
            type: promiseItem.createdBy === userUid ? TYPE_APPLY : TYPE_SCOUT,
            friendly_name: promiseItem.friendlyName,
            unread_count: unreadCount,
            last_date: lastMessage ? lastMessage.dateCreated : promiseItem.dateCreated,
            last_message: messageItem,
        });
    }

    return result;
};


export const countUnreadConversationData = async ({ conversations, userUid }) => {
    if (conversations.length === 0) {
        return {
            all: 0,
            scout: 0,
            apply: 0,
        };
    }

    let scoutCount = 0;
    let applyCount = 0;

    for (let conver of conversations) {
        const lastMessage = conver.lastMessage;
        const participant = await conver.getParticipantByIdentity(userUid);
        const lastReadMessageIndex = participant.lastReadMessageIndex;

        if (lastMessage) {
            const unreadCount = isNil(lastReadMessageIndex) ? lastMessage.index + 1 : lastMessage.index - lastReadMessageIndex;

            if (userUid === conver.createdBy) {
                applyCount += unreadCount;
            } else {
                scoutCount += unreadCount;
            }
        }
    }

    return {
        all: applyCount + scoutCount,
        apply: applyCount,
        scout: scoutCount,
    };
};

export const handlleNewConversationMessagePage = async ({
    eventConversation, userUid,
}) => {
    const dbConversation = await findConversationFromSid({ conversationSid: eventConversation.sid });

    if (!dbConversation) {
        return;
    }

    const newConversation = {
        id: dbConversation.id,
        friendly_name: eventConversation.friendlyName,
        sid: eventConversation.sid,
        type: eventConversation.createdBy === userUid ? TYPE_APPLY : TYPE_SCOUT,
        last_date: eventConversation.dateCreated,
        last_message: null,
        unread_count: 0,
    };

    return newConversation;
};

export const handleLastMessageEventMessagePage = async ({ allConversation, scoutConversation, applyConversation, eventConversation, isCurrent }) => {
    let newAllConversations = [];
    let newScoutConversations = [];
    let newApplyConversations = [];
    const lastMessage = eventConversation.lastMessage;
    const messages = await eventConversation.getMessages(1, lastMessage.index);

    newAllConversations = map(allConversation.items, (allItem) => {
        if (allItem.sid === eventConversation.sid) {
            return {
                ...allItem,
                unread_count: isCurrent ? 0 : allItem.unread_count + 1,
                last_date: lastMessage.dateCreated,
                last_message: messages.items[0].body,
            };
        }

        return {
            ...allItem
        };
    });

    newScoutConversations = map(scoutConversation.items, (scoutItem) => {
        if (scoutItem.sid === eventConversation.sid) {
            return {
                ...scoutItem,
                unread_count: isCurrent ? 0 : scoutItem.unread_count + 1,
                last_date: lastMessage.dateCreated,
                last_message: messages.items[0].body,
            };
        }

        return {
            ...scoutItem
        };
    });

    newApplyConversations = map(applyConversation.items, (applyItem) => {
        if (applyItem.sid === eventConversation.sid) {
            return {
                ...applyItem,
                unread_count: isCurrent ? 0 : applyItem.unread_count + 1,
                last_date: lastMessage.dateCreated,
                last_message: messages.items[0].body,
            };
        }

        return {
            ...applyItem
        };
    });

    return { newAllConversations, newScoutConversations, newApplyConversations };
};

export const handleLastReadIndexEventMessagePage = ({ allConversation, scoutConversation, applyConversation, eventConversation }) => {
    let newAllConversations = [];
    let newScoutConversations = [];
    let newApplyConversations = [];

    newAllConversations = map(allConversation.items, (allItem) => {
        if (allItem.sid === eventConversation.sid) {
            return {
                ...allItem,
                unread_count: 0,
            };
        }

        return {
            ...allItem
        };
    });

    newScoutConversations = map(scoutConversation.items, (scoutItem) => {
        if (scoutItem.sid === eventConversation.sid) {
            return {
                ...scoutItem,
                unread_count: 0,
            };
        }

        return {
            ...scoutItem
        };
    });

    newApplyConversations = map(applyConversation.items, (applyItem) => {
        if (applyItem.sid === eventConversation.sid) {
            return {
                ...applyItem,
                unread_count: 0,
            };
        }

        return {
            ...applyItem
        };
    });

    return { newAllConversations, newScoutConversations, newApplyConversations };
};

export const loadMyNewMessages = async ({ conversations, userUid }) => {
    if (conversations.length === 0) {
        return [];
    }

    const conversationSids = map(conversations, (item) => item.sid);

    const validResponse = await getConversationFromSids({ conversationSids });

    if (!validResponse.success) {
        return [];
    }

    const dbConversations = validResponse.conversations;

    let newMessages = [];

    for (let conver of conversations) {
        const dbConver = find(dbConversations, (item) => item.conversation_id === conver.sid);
        if (!dbConver) {
            continue;
        }

        const lastMessage = conver.lastMessage;
        const participant = await conver.getParticipantByIdentity(userUid);
        const lastReadMessageIndex = participant.lastReadMessageIndex;

        if (!lastMessage) {
            continue;
        }

        const haveUnread = isNil(lastReadMessageIndex) ? lastMessage.index >= 0 : lastMessage.index > lastReadMessageIndex;

        if (!haveUnread) {
            continue;
        }

        const messages = await conver.getMessages(1, lastMessage.index);

        newMessages = [
            ...newMessages,
            {
                id: dbConver.id,
                type: conver.createdBy === userUid ? TYPE_APPLY : TYPE_SCOUT,
                content: `${conver.friendlyName} 様からのメッセージ`,
                date: messages.items[0].dateCreated,
            },
        ];
    }

    return take(orderBy(newMessages, 'date', 'desc'), 5);
};

export const getLastMessageFromTwilioConversation = async ({ conversation }) => {
    const messages = await conversation.getMessages(1);
    return messages.items.length ? messages.items[0] : null;
};
